import { formatNumber } from '@angular/common';
import { AuthService } from 'src/app/services/auth/auth.service';
import { EstadoRegistro, Proveedor, TipoRegistro } from './ia.service';

/**
 * Cómo se escriben los números, los nombres y los errores de la IA en la consola.
 * Un solo lugar: el mismo costo no puede leerse distinto en dos solapas.
 *
 * Copiado en espíritu de `pasarela_consola/lib/ia/formato.ts` (el piloto), con
 * una diferencia pedida para esta consola: el dinero va SIEMPRE con dos
 * decimales. Una llamada suelta cuesta décimas de centavo, y «$0,00» en todas
 * las filas se lee como «gratis»: por eso lo que no llega a un centavo dice
 * «menos de $0,01», que sigue siendo verdad y sigue teniendo dos decimales.
 */

/**
 * 🔴 ¿El campo quedó vacío? Con `type="number"`, `ngModel` devuelve `null` al borrarlo (no `''`), y
 * `Number(null)` es 0: sin esto, vaciar un tope y guardar dejaba un tope de $0 en vez de quitarlo.
 * Hallado por el certificador visual el 2026-09-25.
 */
export const vacio = (v: any): boolean => v === '' || v === null || v === undefined;

/** El número de un campo, o `null` si quedó vacío. */
export const numeroONulo = (v: any): number | null => (vacio(v) ? null : Number(v));

export const usd = (n: number | null | undefined): string => {
  if (n === null || n === undefined || isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v > 0 && v < 0.01) return 'menos de $0,01';
  return `$${formatNumber(v, 'es-VE', '1.2-2')}`;
};

/** Lo mismo que `usd`, para una celda de tabla: lo que no llega a un centavo dice «< $0,01». */
export const usdCorto = (n: number | null | undefined): string => {
  const v = Number(n);
  if (n !== null && n !== undefined && !isNaN(v) && v > 0 && v < 0.01) return '< $0,01';
  return usd(n);
};

export const nf = (n: number | string | null | undefined): string =>
  (n === null || n === undefined || isNaN(Number(n))) ? '—' : formatNumber(Number(n), 'es-VE', '1.0-0');

/**
 * Cómo se escribe una columna numérica de una tabla del asistente. Si el nombre
 * dice que es plata (monto, costo, ticket, inicial…) o alguna celda trae
 * centavos, la columna entera va con dos decimales; si no, sin decimales. Por
 * columna y no por celda: 1.320 y 1.890,50 uno debajo del otro no se comparan.
 */
const PLATA = /monto|usd|\$|precio|costo|ticket|inicial|financ|neto|pendiente|liquid|gasto|dolar|dólar/i;
export const decimalesDeColumna = (columna: string, valores: any[]): string =>
  (PLATA.test(String(columna || '')) || valores.some((v) => typeof v === 'number' && !Number.isInteger(v)))
    ? '1.2-2' : '1.0-0';

export const celda = (v: any, formato: string): string =>
  typeof v === 'number' ? formatNumber(v, 'es-VE', formato) : (v === null || v === undefined ? '—' : String(v));

export const ms = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  if (n < 1000) return `${nf(Math.round(n))} ms`;
  return `${formatNumber(n / 1000, 'es-VE', '1.0-1')} s`;
};

export const fechaHora = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Para las columnas de una tabla: sin el año, que en una bitácora de 90 días sobra. */
export const fechaCorta = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const haceCuanto = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const min = (Date.now() - new Date(iso).getTime()) / 60000;
  if (isNaN(min)) return '';
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${Math.round(min)} min`;
  const h = min / 60;
  if (h < 48) return `hace ${Math.round(h)} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d === 1 ? '' : 's'}`;
};

/** Un `updatedAt` en la época 0 es «nunca», no el 31/12/1969 (visto en el piloto). */
export const esFechaReal = (iso: string | null | undefined): boolean =>
  !!iso && new Date(iso).getTime() > 86400000;

// ── Nombres ──────────────────────────────────────────────────────────────────
//
// ⚠️ ESTA TABLA NO ES LA LISTA DE AGENTES: la lista la manda el servidor. Esto
// sólo traduce los que ya conocemos; uno nuevo aparece igual, humanizado.

const AGENTES: { [k: string]: { titulo: string; hace: string } } = {
  'asistente-comercio': {
    titulo: 'Asistente del comercio',
    hace: 'Le contesta al administrador del comercio, en su portal, sobre sus ventas, sus órdenes, sus turnos, '
        + 'sus liquidaciones y qué le falta cargar en su ficha.',
  },
  'examen': {
    titulo: 'Examen de certificación',
    hace: 'Las preguntas de prueba con respuesta esperada que certifican al asistente. No contesta a nadie.',
  },
  'asistente-creditotal': {
    titulo: 'Asistente de CrediTotal',
    hace: 'Reservado para la consola interna de CrediTotal. Todavía no tiene código: prenderlo hoy no cambia nada.',
  },
};

export const tituloAgente = (agente: string): string => {
  if (AGENTES[agente]) return AGENTES[agente].titulo;
  const limpio = String(agente || '').replace(/[-_]/g, ' ');
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
};

export const queHaceAgente = (agente: string): string | null => AGENTES[agente]?.hace ?? null;

export const PROVEEDORES: { id: Proveedor; nombre: string }[] = [
  { id: 'google', nombre: 'Google (Gemini)' },
  { id: 'anthropic', nombre: 'Anthropic (Claude)' },
  { id: 'openai', nombre: 'OpenAI' },
];

export const nombreProveedor = (p: string | null | undefined): string =>
  PROVEEDORES.find((x) => x.id === p)?.nombre || (p || '—');

/** De dónde sale la clave que se está usando (§3). */
export const ORIGEN: { [k: string]: string } = {
  comercio: 'Clave propia del comercio',
  consola: 'Cargada en esta consola',
  entorno: 'Variable del servidor',
};

export const ETIQUETA_ESTADO: { [k in EstadoRegistro]: string } = {
  OK: 'Exitoso',
  FALLA: 'Error',
  OMITIDO: 'Omitido',
};

/** OMITIDO no es rojo: no falló nada, el agente estaba apagado o se llegó al tope. */
export const BADGE_ESTADO: { [k in EstadoRegistro]: string } = {
  OK: 'kb-badge-exito',
  FALLA: 'kb-badge-peligro',
  OMITIDO: 'kb-badge-neutro',
};

/**
 * Los tres tipos de fila de `ia_decisiones`, como los escribe el núcleo:
 *   · `caso`    — UNA pregunta del administrador en su portal, con su respuesta y su costo total;
 *   · `llamada` — cada ida al modelo (OK, FALLA u OMITIDO): de aquí sale el gasto del día;
 *   · `prueba`  — una pregunta hecha desde «Probar» en esta consola.
 * (El «banco de casos» del examen es OTRA colección, `ia_casos`: no confundir.)
 */
export const ETIQUETA_TIPO: { [k in TipoRegistro]: string } = {
  caso: 'Pregunta',
  llamada: 'Llamada al modelo',
  prueba: 'Prueba',
};

/**
 * El rótulo de UNA fila: el examen de certificación (agente `examen`) escribe filas `caso` con su
 * propia «pregunta» («examen de asistente-comercio (sinModelo)»). No es una pregunta de nadie y no
 * se rotula como tal. Visto en vivo contra el servidor de prueba el 2026-09-25.
 */
export const etiquetaFila = (r: { agente?: string | null; tipo?: string | null }): string =>
  r?.agente === 'examen' ? 'Examen' : ((ETIQUETA_TIPO as any)[r?.tipo || ''] || r?.tipo || '—');

/**
 * El motivo de una fila OMITIDO o FALLA, en castellano. El motor escribe códigos
 * (`dominio/apagado.js`, `infra/modelo.js`: `sin_credencial:google`,
 * `comercio_deshabilitado`, `tope_diario_comercio:gastoHoy=…`) que nadie de negocio
 * lee. Lo que no se conoce se muestra tal cual: nunca se esconde un motivo.
 */
const MOTIVOS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^apagado_plataforma/, () => 'La IA estaba apagada para todos'],
  [/^modelo_fuera_de_catalogo:(.*)$/, (m) => `El modelo «${m[1]}» no está en el catálogo`],
  [/^sin_credencial:(\w+)/, (m) => `No había clave de ${nombreProveedor(m[1])}`],
  [/^credencial_invalida:(\w+)/, (m) => `La clave de ${nombreProveedor(m[1])} no sirve`],
  [/^proveedor_desconocido:(.*)$/, (m) => `Proveedor desconocido: ${m[1]}`],
  [/^agente_deshabilitado$/, () => 'El agente estaba apagado'],
  [/^sin_comercio$/, () => 'La llamada llegó sin comercio'],
  [/^comercio_deshabilitado$/, () => 'El comercio tiene el asistente apagado'],
  [/^tope_diario_plataforma/, () => 'Se llegó al tope diario de plataforma'],
  [/^tope_diario_agente/, () => 'Se llegó al tope diario del agente'],
  [/^tope_diario_comercio/, () => 'Se llegó al tope diario del comercio'],
  [/^peticion_incompleta$/, () => 'La consulta al modelo llegó incompleta'],
  [/^tiempo_agotado$/, () => 'Se acabó el tiempo para contestar'],
  [/^cadena_de_respaldo_sin_credenciales$/, () => 'Ningún modelo de respaldo tenía clave'],
  [/^error_interno/, () => 'Error interno del servidor'],
  [/^(sin_hechos|error_desconocido)$/, () => 'Error desconocido'],
];

/**
 * Lo que contesta un PROVEEDOR cuando algo sale mal viene crudo y en inglés («API key is invalid.»,
 * «RESOURCE_EXHAUSTED»…). Se traduce por familia; el texto crudo queda sólo en un `title`.
 */
const DEL_PROVEEDOR: [RegExp, string][] = [
  [/api[ _-]?key|invalid.*key|key.*(invalid|not valid)|unauthori[sz]ed|authentication|\b401\b|API_KEY_INVALID/i, 'La clave no es válida.'],
  [/quota|rate[ _-]?limit|too many requests|\b429\b|RESOURCE_EXHAUSTED|exceeded|insufficient.*(credit|balance|funds)|billing/i, 'Se agotó la cuota del proveedor.'],
  [/permission|forbidden|\b403\b|PERMISSION_DENIED|not allowed|access denied/i, 'La clave no tiene permiso para ese modelo.'],
  [/time[ _-]?out|timed out|abort|deadline|ETIMEDOUT/i, 'El proveedor no contestó a tiempo.'],
  [/not found|\b404\b|does not exist|unknown model|model.*(unsupported|not supported)/i, 'El proveedor no reconoce ese modelo.'],
  [/ENOTFOUND|ECONNREFUSED|ECONNRESET|network|fetch failed|socket/i, 'No se pudo llegar al proveedor.'],
];

const traducirDelProveedor = (m: string): string | null => {
  for (const [re, t] of DEL_PROVEEDOR) if (re.test(m)) return t;
  return null;
};

export const motivoLegible = (motivo: string | null | undefined): string => {
  const m = String(motivo || '').trim();
  if (!m) return '';
  for (const [re, f] of MOTIVOS) { const r = m.match(re); if (r) return f(r); }
  // Los que arma la guardia del asistente vienen unidos por « || »: se separan con « · ».
  const partes = m.split(/\s*\|\|\s*/).filter(Boolean);
  if (partes.length > 1) return partes.join(' · ');
  return traducirDelProveedor(m) || m;
};

/** Las partes de un motivo compuesto, una por renglón (para el detalle de la bitácora). */
export const partesDelMotivo = (motivo: string | null | undefined): string[] => {
  const m = String(motivo || '').trim();
  if (!m) return [];
  const partes = m.split(/\s*\|\|\s*/).filter(Boolean);
  return partes.length > 1 ? partes : [motivoLegible(m)];
};

/**
 * El error de un proveedor (prueba de clave, «No contesta»), en castellano y sin dejar nunca el
 * inglés crudo a la vista: lo que no se reconoce dice «El proveedor rechazó la prueba.».
 */
export const errorDeProveedor = (error: string | null | undefined): string => {
  const m = String(error || '').trim();
  if (!m) return 'El proveedor rechazó la prueba.';
  for (const [re, f] of MOTIVOS) { const r = m.match(re); if (r) return f(r); }
  return traducirDelProveedor(m) || 'El proveedor rechazó la prueba.';
};

export const CAUSAS: { id: 'MANTENIMIENTO' | 'COSTO' | 'INCIDENTE' | 'REVISION'; nombre: string }[] = [
  { id: 'INCIDENTE', nombre: 'Incidente' },
  { id: 'COSTO', nombre: 'Gasto' },
  { id: 'MANTENIMIENTO', nombre: 'Mantenimiento' },
  { id: 'REVISION', nombre: 'Revisión' },
];

export const nombreCausa = (c: string | null | undefined): string =>
  CAUSAS.find((x) => x.id === c)?.nombre || (c || '');

// ── Errores ──────────────────────────────────────────────────────────────────

/**
 * El error, en una línea que se pueda leer.
 *
 * 🔴 Un 403 en una ESCRITURA dice «No tienes permiso para cambiar esto» y la
 * pantalla sigue (la puerta de verdad es del servidor, §7.2). Un 404 de la ruta
 * entera casi siempre es que el núcleo todavía no tiene el módulo montado.
 */
export const errorDeIA = (e: any, escritura = false): string => {
  const status = Number(e?.status ?? 0);
  if (status === 403) return escritura ? 'No tienes permiso para cambiar esto.' : 'No tienes permiso para ver esto.';
  const cuerpo = e?.error;
  const delServidor = (cuerpo && typeof cuerpo === 'object') ? (cuerpo.error || cuerpo.msg) : null;
  if (delServidor && typeof delServidor === 'string') return delServidor;
  if (status === 0) return 'No se pudo conectar con el servidor.';
  if (status === 404) return 'El servidor todavía no tiene esta función (respondió 404).';
  if (status === 401) return 'La sesión no es válida.';
  if (status >= 500) return `El servidor tuvo un error (${status}).`;
  return escritura ? 'No se pudo guardar.' : 'No se pudo cargar.';
};

// ── Permisos (los DOS sistemas) ──────────────────────────────────────────────
//
// 🔴 `hasPermission` devuelve `true` a quien NO tiene RBAC, así que sin RBAC
// manda el perfil viejo (OWNER, como las rutas de Comercios).
//
// 📍 Con RBAC, los permisos propios son `config.ia.read` y `config.ia.update`.
// ⚠️ Todavía no existen en el catálogo ni en la matriz de `roles-permisos`
// (memoria `rbac-comodines-y-matriz-hardcodeada`: un permiso nuevo no le llega a
// nadie hasta tocar el seed, el ROLE_PERMISSION_MAP y `systemModules`). Mientras
// tanto, quien puede editar comercios (`config.comercios.update`) también ve y
// configura la IA: es la misma gente que prende y apaga a un comercio. Cuando
// `config.ia.*` esté dado de alta, se quita ese puente de aquí y de nada más.

const PERFILES_IA = ['OWNER'];

export const puedeVerIA = (auth: AuthService): boolean =>
  auth.hasRbac$.getValue()
    ? auth.hasAnyPermission(['config.ia.read', 'config.ia.update', 'config.comercios.update'])
    : auth.IsPermitid(PERFILES_IA);

export const puedeConfigurarIA = (auth: AuthService): boolean =>
  auth.hasRbac$.getValue()
    ? auth.hasAnyPermission(['config.ia.update', 'config.comercios.update'])
    : auth.IsPermitid(PERFILES_IA);
